import { useState, useEffect } from 'react';
import { clientsApi, authApi } from '../services/api';

function useClients() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchClients = async () => {
    try {
      console.log('1. Intentando obtener clientes...');
      setLoading(true);
      const data = await clientsApi.getAll();
      console.log('2. Datos de clientes recibidos:', data);

      if (!Array.isArray(data)) {
        console.error('Los datos de clientes no son un array:', data);
        setClients([]);
        return;
      }

      const processedClients = data.map(client => ({
        ...client,
        id: client._id // Asegurarnos que cada cliente tenga un id para el grid
      }));

      console.log('3. Clientes procesados:', processedClients);
      setClients(processedClients);
      setError(null);
    } catch (error) {
      console.error('Error al obtener clientes:', error);
      setError(error.message || 'Error al cargar clientes');
    } finally {
      setLoading(false);
    }
  };

  // Función para obtener companyId usando el servicio de authApi
  const getCompanyId = async () => {
    try {
      console.log('Intentando obtener datos de usuario y compañía...');
      const userData = await authApi.getMe();
      
      console.log('Datos de usuario obtenidos:', userData);
      
      if (!userData.success || !userData.company || !userData.company.id) {
        console.error('Respuesta inválida de authApi.getMe():', userData);
        throw new Error('No se encontró información de la empresa');
      }
      
      console.log('CompanyId obtenido:', userData.company.id);
      return userData.company.id;
    } catch (error) {
      console.error('Error al obtener companyId:', error);
      throw error;
    }
  };

  const saveClient = async (client) => {
    try {
      console.log('4. Cliente a guardar:', client);
      
      // Asegurarse de que los campos necesarios estén presentes
      if (!client.nombre) throw new Error('El nombre es requerido');
      if (!client.rif) throw new Error('El RIF/Cédula es requerido');
      if (!client.email) throw new Error('El email es requerido');
      if (!client.telefono) throw new Error('El teléfono es requerido');
      if (!client.direccion) throw new Error('La dirección es requerida');
      
      // Obtener el companyId a través de la API
      const companyId = await getCompanyId();
      
      // Preparar el objeto cliente incluyendo el companyId
      const clientToSave = {
        ...client,
        companyId,
        
        // Asegurar que campos numéricos sean números
        diasCredito: Number(client.diasCredito || 0),
        limiteCredito: Number(client.limiteCredito || 0),
        
        // Garantizar que tipoPersona y tipoCliente estén presentes
        tipoPersona: client.tipoPersona || 'natural',
        tipoCliente: client.tipoCliente || 'regular'
      };
      
      console.log('5. Cliente preparado para guardar con companyId:', clientToSave);
      
      // Determinar si es una creación o actualización
      let savedClient;
      if (client._id) {
        console.log(`7. Actualizando cliente con ID: ${client._id}`);
        savedClient = await clientsApi.update(client._id, clientToSave);
      } else {
        console.log('7. Creando nuevo cliente');
        savedClient = await clientsApi.create(clientToSave);
      }
      
      console.log('8. Cliente guardado con éxito:', savedClient);
      return savedClient;
    } catch (error) {
      console.error('Error al guardar cliente:', error);
      throw error;
    }
  };

  const deleteClient = async (id) => {
    try {
      console.log(`Eliminando cliente con ID: ${id}`);
      await clientsApi.delete(id);
      return { success: true };
    } catch (error) {
      console.error('Error al eliminar cliente:', error);
      throw error;
    }
  };

  // Cargar clientes al montar el componente
  useEffect(() => {
    console.log('useEffect en useClients - Llamando a fetchClients');
    fetchClients();
  }, []);

  // Suma un cliente al listado que ya está en memoria, sin volver a pedirlo al
  // servidor. fetchClients pone loading en true, y las pantallas que muestran un
  // spinner mientras carga desmontan lo que el usuario tenga abierto: al crear un
  // cliente desde el formulario de factura, eso borraba la factura a medio cargar.
  const agregarClienteLocal = (cliente) => {
    if (!cliente || !cliente._id) return;
    setClients(prev => [cliente, ...prev.filter(c => c._id !== cliente._id)]);
  };

  return { clients, loading, error, saveClient, deleteClient, fetchClients, agregarClienteLocal };
}

// Exportación nombrada
export { useClients };

// Exportación por defecto
export default useClients;